/**
 * ProjectPickingCard — Wave 30 Chunk 11b.
 *
 * One project can have many picking lists (e.g. per install phase).
 * For each list, shows the active rows with:
 *   • qty_required / qty_picked / qty_returned / qty_outstanding
 *   • inline +/− buttons to log pick + return movements
 *   • status pill (pending / in_progress / partial / picked / returned)
 *   • non-stock items kept in their own visual lane (purple chip)
 *   • shortfall highlighted in amber when qty_outstanding > 0
 *
 * Power moves:
 *   • "Pull from quote" → POST /generate-from-quote (additive, de-dupes)
 *   • "Pull from sales order" → POST /generate-from-quote (IQ Retail sales order import)
 *   • "Final breakdown" → opens a modal with invoice-ready summary
 *     grouped by section (qty_actually_used = picked − returned).
 *
 * This is the book of record for what came off the shelf. Every change
 * writes a movement row server-side so we can reconstruct history.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  PackageCheck, Plus, Minus, RefreshCw, Loader2, AlertTriangle,
  ChevronDown, ChevronRight, FileText, Archive, X, ListChecks,
  History, ArrowDownToLine, ArrowUpFromLine,
  Camera, Trash2, PenLine, ExternalLink,
  Search, Check, Link2, GitMerge, GitBranch,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import { displayPickingListName } from "../../lib/pickingListName";
import SignaturePad from "../SignaturePad";
import { uploadUrl } from "../../lib/uploads";
import NewPickingSlipModal from "../picking/NewPickingSlipModal";
import DictTextarea from "../DictTextarea";
import { EditLineButton, MergedLinesPanel, mergedCount } from "../procurement/LineMergeTools";

const ITEM_STATUS = {
  pending:     { c: "#647087", l: "Pending" },
  in_progress: { c: "#1FB6C1", l: "In progress" },
  partial:     { c: "#F59E0B", l: "Partial" },
  picked:      { c: "#10B981", l: "Picked" },
  returned:    { c: "#A78BFA", l: "Returned" },
  cancelled:   { c: "#647087", l: "Cancelled" },
};

export default function ProjectPickingCard({ projectId, focusListId, singleListId, onListChanged }) {
  // Iter 290za — when `singleListId` is set we render in "focused"
  // standalone mode: no multi-list picker, no "New list" button, and
  // we DON'T pull the project's list of lists. This lets the new
  // /picking/lists/:lid page reuse this component without duplicating
  // the ~800-line item table / movements / breakdown logic.
  const _focusedMode = !!singleListId;
  const _initialActive = singleListId || focusListId || null;
  const [lists, setLists] = useState([]);
  const [activeId, setActiveId] = useState(_initialActive);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [viewMode, setViewMode] = useState("items");        // 'items' | 'breakdown'
  const [showAddRow, setShowAddRow] = useState(false);
  const [rowFilter, setRowFilter] = useState("all");        // 'all' | 'outstanding' | 'partial' | 'complete'
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);   // item id whose history is expanded inline
  // 2026-02-18 Round B — sales-order-only picking. We pre-fetch whether
  // the project has at least one sales_order so we can disable the
  // "Pull from sales order" button with a helpful tooltip instead of
  // letting the user click and get a 400 toast.
  const [salesOrderCount, setSalesOrderCount] = useState(null);
  const [sourceDocs, setSourceDocs] = useState([]);   // [{id, document_number, category}]
  // Iter 290zl — picking rows can link back to a job card so manual
  // additions surface in the JC materials list. We resolve the JC
  // title/short_id once per detail load and pass it down to rows.
  const [linkedJCs, setLinkedJCs] = useState([]);   // [{id, title, short_id}]
  // Iter 382 — "New picking slip" wizard (manual / SO / drag-drop OCR).
  const [newOpen, setNewOpen] = useState(false);

  const loadLists = async () => {
    // In focused mode we skip the project's list-of-lists load entirely.
    if (_focusedMode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/picking/lists`, { params: { limit: 500, project_id: projectId || undefined } });
      const items = r.data?.items || [];
      setLists(items);
      if (!activeId && items.length) setActiveId(items[0].id);
    } catch (err) {
      // module disabled → silently no-op
      setLists([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSalesOrderCount = async () => {
    // Cheap probe so we know whether to enable "Pull from sales order".
    // In focused mode we may not have a projectId — fall through to
    // the detail-driven equivalent (see useEffect below).
    if (!projectId) {
      setSalesOrderCount(0);
      setSourceDocs([]);
      return;
    }
    try {
      const r = await api.get(`/sales-documents`, {
        params: { project_id: projectId, category: "sales_order", limit: 50 },
      });
      const rows = r.data?.items || r.data || [];
      setSalesOrderCount(Array.isArray(rows) ? rows.length : 0);
      setSourceDocs(
        (Array.isArray(rows) ? rows : []).map((sd) => ({
          id: sd.id, document_number: sd.document_number, category: sd.category,
        })),
      );
    } catch (err) {
      setSalesOrderCount(0);
      setSourceDocs([]);
    }
  };

  // Iter 401.3 — 2026-02-20 · Focused-mode fix. When a picking list is
  // opened standalone (no projectId) but has `source_sales_doc_ids`
  // recorded (e.g. created via SO drag-drop scan or linked to an SO
  // after the fact), resolve those SO records so the Pull-from-sales-
  // order button becomes ENABLED and functional. Previously the
  // focused-mode picking page always saw `salesOrderCount=0` because
  // `loadSalesOrderCount` requires a project_id, so clicking Pull did
  // nothing (button disabled) OR hit /generate-from-quote which 400s
  // on lists with no project_id.
  const loadSourceDocsFromDetail = async (d) => {
    if (!d || projectId) return; // project-mode already handled above
    const ids = d.source_sales_doc_ids || [];
    if (!ids.length) {
      setSalesOrderCount(0);
      setSourceDocs([]);
      return;
    }
    try {
      const results = await Promise.all(
        ids.map((sid) => api.get(`/sales-documents/${sid}`).then((r) => r.data).catch(() => null)),
      );
      const docs = results
        .filter(Boolean)
        .map((sd) => ({ id: sd.id, document_number: sd.document_number, category: sd.category }));
      setSourceDocs(docs);
      setSalesOrderCount(docs.length);
    } catch {
      setSalesOrderCount(0);
      setSourceDocs([]);
    }
  };

  const loadDetail = async (lid) => {
    if (!lid) { setDetail(null); setLinkedJCs([]); return; }
    try {
      const r = await api.get(`/picking/lists/${lid}`);
      setDetail(r.data);
      // Iter 401.3 — refresh focused-mode source SOs so Pull enables.
      await loadSourceDocsFromDetail(r.data);
      // Iter 290zl — fetch JC titles for the linked sibling jobcard_ids
      // so the "Link to JC" picker can show names, not opaque hex IDs.
      const jcIds = r.data?.jobcard_ids || [];
      if (jcIds.length === 0) {
        setLinkedJCs([]);
      } else {
        try {
          const jcResults = await Promise.all(
            jcIds.map((jid) =>
              api.get(`/jobcards/${jid}`).then((rr) => rr.data).catch(() => null),
            ),
          );
          setLinkedJCs(
            jcResults
              .filter(Boolean)
              .map((jc) => ({ id: jc.id, title: jc.title, short_id: jc.short_id })),
          );
        } catch {
          setLinkedJCs([]);
        }
      }
    } catch (err) {
      setDetail(null);
      setLinkedJCs([]);
    }
  };

  useEffect(() => { loadLists(); loadSalesOrderCount(); }, [projectId]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetail(activeId); }, [activeId]);

  useEffect(() => {
    // Auto-focus a newly generated list when the parent says so.
    if (focusListId && focusListId !== activeId) {
      setActiveId(focusListId);
      loadLists();
    }
  }, [focusListId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const newList = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/picking/lists`, {
        project_id: projectId,
        name: `Picking · ${new Date().toLocaleDateString("en-GB")}`,
      });
      setActiveId(r.data.id);
      await loadLists();
      toast.success("Picking list created.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create list.");
    } finally {
      setBusy(false);
    }
  };

  const pullFromQuote = async () => {
    if (!activeId) {
      toast.error("No picking list selected.");
      return;
    }
    // 2026-02-18 Round B → Iter 410.4 · never fail silently. Every
    // code path from here MUST produce a toast, otherwise the user
    // just sees a dead button.
    if (salesOrderCount === 0) {
      toast.error(
        "No sales order on this list yet. Link a sales order first (drag-drop an SO onto this picking list, or use 'Send to Picking' from the SO card).",
      );
      return;
    }
    setBusy(true);
    try {
      const listHasProject = detail?.project_id;
      let addedTotal = 0;
      let attempted = 0;
      if (listHasProject) {
        const r = await api.post(`/picking/lists/${activeId}/generate-from-quote`);
        addedTotal = r.data?.added || 0;
        attempted = 1;
      } else {
        // Prefer the freshly-hydrated sourceDocs; fall back to the raw
        // source_sales_doc_ids off `detail` in case sourceDocs missed
        // a doc (e.g. a 404'd fetch).
        let docIds = sourceDocs.map((d) => d.id);
        if (!docIds.length && (detail?.source_sales_doc_ids || []).length) {
          docIds = [...detail.source_sales_doc_ids];
          toast.info(`Retrying with ${docIds.length} raw linked doc ID(s)…`);
        }
        if (!docIds.length) {
          toast.error(
            "This picking list has no source sales order linked. Link one (SO drag-drop) then try again.",
          );
          setBusy(false);
          return;
        }
        for (const sid of docIds) {
          attempted += 1;
          try {
            const r = await api.post(`/picking/lists/${activeId}/pull-from-sales-doc`, {
              sales_doc_id: sid,
            });
            addedTotal += r.data?.added || 0;
          } catch (perDocErr) {
            // Never let one bad doc silently swallow the whole flow.
            toast.error(
              `SO #${sid.slice(0, 8)} pull failed: ${
                perDocErr?.response?.data?.detail || perDocErr?.message || "unknown"
              }`,
            );
          }
        }
      }
      if (addedTotal > 0) {
        toast.success(
          `Pulled ${addedTotal} row${addedTotal === 1 ? "" : "s"} from ${attempted} sales order${attempted === 1 ? "" : "s"}.`,
        );
      } else if (attempted > 0) {
        toast.info(
          `Nothing new — all lines from ${attempted} sales order${attempted === 1 ? "" : "s"} are already on this picking list.`,
        );
      }
      loadDetail(activeId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Pull failed.");
    } finally {
      setBusy(false);
    }
  };

  // Iter 290zp.13z15 — `pullFromPlanner` and the planner→picking
  // pipeline are gone. Picking is fed from real IQ Retail-imported
  // sales orders only (via `pullFromSalesOrder` below). The legacy
  // `POST /picking/lists/{lid}/generate-from-planner` endpoint is
  // soft-deprecated server-side.

  const archive = async () => {
    if (!activeId) return;
    if (!window.confirm("Archive this picking list?")) return;
    try {
      await api.delete(`/picking/lists/${activeId}`);
      toast.success("Archived.");
      setActiveId(null);
      await loadLists();
    } catch (err) {
      toast.error("Archive failed.");
    }
  };

  const movement = async (iid, kind, qty, note = "", extras = {}) => {
    if (!qty || qty <= 0) return;
    try {
      await api.post(`/picking/items/${iid}/${kind}`, { qty, note, ...extras });
      loadDetail(activeId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${kind} failed.`);
    }
  };

  const deleteItem = async (iid) => {
    if (!window.confirm("Remove this row?")) return;
    try {
      await api.delete(`/picking/items/${iid}`);
      loadDetail(activeId);
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const openBreakdown = async () => {
    if (viewMode === "breakdown") { setViewMode("items"); return; }
    try {
      const r = await api.get(`/picking/lists/${activeId}/breakdown`);
      setBreakdown(r.data);
      setViewMode("breakdown");
    } catch (err) {
      toast.error("Breakdown failed.");
    }
  };

  return (
    <div className="hv-card p-4" data-testid="project-picking-card">
      {/* Header */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <PackageCheck className="w-4 h-4 text-[#1FB6C1]" />
          <h3 className="font-display text-base font-bold">Picking</h3>
          {lists.length > 0 && (
            <span className="text-[10px] text-[#647087]">· {lists.length} list{lists.length === 1 ? "" : "s"}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {!_focusedMode && (
            <button
              type="button"
              onClick={() => setNewOpen(true)}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-[11px]"
              data-testid="picking-new-list"
            >
              <Plus className="w-3 h-3" /> New list
            </button>
          )}
        </div>
      </div>

      {loading && <div className="text-xs text-[#647087]"><Loader2 className="w-3 h-3 inline animate-spin" /> Loading…</div>}

      {/* List picker — hidden in focused mode (single-list standalone page) */}
      {!loading && !_focusedMode && lists.length > 0 && (() => {
        // Iter 290zn — build a JC-map for the legacy-name fallback. We
        // only have detail-loaded JC info for the ACTIVE list (linkedJCs);
        // other tabs render their stored name as-is. That's acceptable
        // for the tab strip — the full upgrade lands once a tab is
        // opened. Storage migration (POST /api/picking/admin/rename-
        // legacy-names) finalises everything.
        const jcMap = {};
        (linkedJCs || []).forEach((jc) => { if (jc?.id) jcMap[jc.id] = jc; });
        return (
        <div className="flex gap-1.5 mb-3 overflow-x-auto pb-1">
          {lists.map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => setActiveId(l.id)}
              className={`shrink-0 px-2.5 py-1 rounded text-[11px] border ${
                activeId === l.id
                  ? "bg-[#1FB6C1]/20 border-[#1FB6C1] text-white"
                  : "bg-white/[0.02] border-white/5 text-[#a0aabf] hover:bg-white/[0.04]"
              }`}
              data-testid={`picking-list-tab-${l.id}`}
            >
              {displayPickingListName(l, jcMap)}
              {l.status && <span className="ml-1 text-[9px] text-[#647087]">· {l.status}</span>}
              {/* [Iter 420] Family-tree provenance chip. Only shown
                  when the list didn't have a direct project_id — i.e.
                  it was surfaced because of a linked quote/SO/JC. */}
              {l.linked_via && l.linked_via.kind === "sales_document" && (
                <span
                  className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#A78BFA]/15 text-[#A78BFA]"
                  title={`Linked via ${l.linked_via.category || "sales document"}`}
                  data-testid={`picking-list-linkedvia-${l.id}`}
                >
                  via {l.linked_via.document_number || l.linked_via.category || "doc"}
                </span>
              )}
              {l.linked_via && l.linked_via.kind === "jobcard" && (
                <span
                  className="ml-1 text-[9px] px-1 py-0.5 rounded bg-[#1FB6C1]/15 text-[#1FB6C1]"
                  title={`Linked via job card ${l.linked_via.short_id || ""}`}
                  data-testid={`picking-list-linkedvia-${l.id}`}
                >
                  via JC {l.linked_via.short_id || l.linked_via.title || "linked"}
                </span>
              )}
            </button>
          ))}
        </div>
        );
      })()}

      {!loading && !_focusedMode && lists.length === 0 && (
        <div className="text-xs text-[#647087] text-center py-3">
          No picking lists yet. Create one, then pull from quote or planner.
        </div>
      )}

      {/* Active list detail */}
      {detail && (
        <div className="space-y-3" data-testid={`picking-detail-${detail.id}`}>
          {/* Action toolbar */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={pullFromQuote}
              disabled={busy || salesOrderCount === 0}
              className="hv-btn hv-btn-ghost text-[11px] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="picking-pull-quote"
              title={
                salesOrderCount === 0
                  ? "No sales order on this project yet — convert a quote to a sales order first."
                  : "Pull line items from this project's sales order(s)"
              }
            >
              <RefreshCw className="w-3 h-3" /> Pull from sales order
              {salesOrderCount > 0 && (
                <span className="ml-1 opacity-60 font-mono">({salesOrderCount})</span>
              )}
            </button>
            <button onClick={() => setShowAddRow((v) => !v)}
                    className={`hv-btn text-[11px] ${showAddRow ? "hv-btn-primary" : "hv-btn-ghost"}`}
                    data-testid="picking-add-row">
              <Plus className="w-3 h-3" /> Add row
            </button>
            <button onClick={openBreakdown}
                    className={`hv-btn text-[11px] ${viewMode === "breakdown" ? "hv-btn-primary" : "hv-btn-ghost"}`}
                    data-testid="picking-breakdown">
              <FileText className="w-3 h-3" /> {viewMode === "breakdown" ? "Back to items" : "Breakdown"}
            </button>
            <button onClick={archive} className="hv-btn hv-btn-ghost text-[11px] ml-auto"
                    data-testid="picking-archive">
              <Archive className="w-3 h-3" /> Archive
            </button>
            {/* [Iter 420] Open the picking slip in the dedicated Picking
                module (full sign-off, print, share, etc.). Mirrors the
                "Open full" affordance on the Job Cards inline card. */}
            <Link
              to={`/picking/${detail.id}`}
              className="hv-btn hv-btn-primary text-[11px]"
              data-testid={`picking-open-full-${detail.id}`}
              title="Open in the Picking module"
            >
              <ExternalLink className="w-3 h-3" /> Open full
            </Link>
          </div>

          {/* Inline add-row form — multi-add keeps panel open after each
              successful save, only closes when user hits "Done". */}
          {showAddRow && (
            <InlineAddRow
              listId={detail.id}
              onCancel={() => { setShowAddRow(false); loadDetail(detail.id); }}
              onAdded={({ keepOpen } = {}) => { loadDetail(detail.id); if (!keepOpen) setShowAddRow(false); }}
            />
          )}

          {/* Totals strip — Iter 290zc: progress bar + status counters.
              Progress = rows complete out of total rows. */}
          {(() => {
            const tot = detail.totals || {};
            const rows = tot.rows || 0;
            const done = tot.rows_complete || 0;
            const partial = tot.rows_partial || 0;
            const outstanding = tot.rows_outstanding || 0;
            const pct = rows ? Math.round((done / rows) * 100) : 0;
            return (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="uppercase tracking-wider text-[#647087] font-bold">
                    Progress · {done}/{rows} complete
                  </span>
                  <span className="font-mono text-cyan-300">{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-white/[0.05] overflow-hidden" data-testid="picking-progress-bar">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2 text-[10px]">
                  <Stat label={`Complete (${done})`} value={done} accent="#10B981" />
                  <Stat label={`Partial (${partial})`} value={partial} accent={partial > 0 ? "#F59E0B" : undefined} />
                  <Stat label={`Outstanding (${outstanding})`} value={outstanding} accent={outstanding > 0 ? "#F43F5E" : undefined} warn={outstanding > 0} />
                </div>
                {/* Row status filter chips */}
                <div className="flex gap-1 flex-wrap" data-testid="picking-row-filter">
                  {[
                    { v: "all",         l: `All (${rows})` },
                    { v: "outstanding", l: `Outstanding (${outstanding})` },
                    { v: "partial",     l: `Partial (${partial})` },
                    { v: "complete",    l: `Complete (${done})` },
                  ].map((c) => (
                    <button
                      key={c.v}
                      type="button"
                      onClick={() => setRowFilter(c.v)}
                      className={`shrink-0 px-2.5 py-0.5 rounded-full text-[10px] border ${
                        rowFilter === c.v
                          ? "bg-cyan-500/20 border-cyan-400/60 text-white"
                          : "bg-white/[0.02] border-white/5 text-[#a0aabf] hover:bg-white/[0.04]"
                      }`}
                      data-testid={`picking-row-filter-${c.v}`}
                    >
                      {c.l}
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* 2026-02-18 Round B — source-doc rail. When picking rows
              were pulled from one or more sales orders, surface the SO
              numbers + click-through so the user can audit the origin. */}
          {(detail.source_sales_doc_ids || []).length > 0 && (
            <div
              className="rounded border border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.05] px-2.5 py-1.5 text-[10px] text-[#a0aabf] flex items-start gap-1.5 flex-wrap"
              data-testid={`picking-source-rail-${detail.id}`}
            >
              <span className="text-[#1FB6C1] font-bold uppercase tracking-wider">Source:</span>
              {(detail.source_sales_doc_ids || []).map((sid) => {
                const sd = sourceDocs.find((x) => x.id === sid);
                const label = sd?.document_number || `SO-${sid.slice(0, 8)}`;
                return (
                  <a
                    key={sid}
                    href={`/sales-documents/${sid}/edit`}
                    className="text-[#1FB6C1] hover:underline font-mono"
                    data-testid={`picking-source-link-${sid}`}
                  >
                    ↗ {label}
                  </a>
                );
              })}
            </div>
          )}

          {/* Iter 290zh — Bulk action bar: best-in-class warehouse pattern.
              Three primary actions visible at all times so the operator
              can rip through pick → dispatch → close in three taps.
              On mobile this is duplicated as a sticky bottom bar (below). */}
          {viewMode === "items" && detail.items?.length > 0 && (
            <BulkActionBar
              listId={detail.id}
              totals={detail.totals || {}}
              status={detail.status}
              onChanged={() => loadDetail(detail.id)}
            />
          )}

          {/* Switchable content: items grid OR final breakdown (inline, not a modal) */}
          {viewMode === "items" ? (
            <PickingRows items={(detail.items || []).filter((it) => {
                           const out = it.qty_outstanding || 0;
                           const picked = it.qty_picked || 0;
                           if (rowFilter === "outstanding") return out > 0;
                           if (rowFilter === "partial")     return picked > 0 && out > 0;
                           if (rowFilter === "complete")    return out <= 0 && (it.qty_required || 0) > 0;
                           return true;
                         })}
                         onDelete={deleteItem}
                         onRefresh={() => { loadDetail(activeId); onListChanged?.(); }}
                         linkedJCs={linkedJCs}
                         expandedHistoryId={expandedHistoryId}
                         onToggleHistory={(iid) => setExpandedHistoryId((cur) => cur === iid ? null : iid)} />
          ) : (
            breakdown && <BreakdownInline data={breakdown} />
          )}
        </div>
      )}
      {newOpen && (
        <NewPickingSlipModal
          open={newOpen}
          onClose={() => setNewOpen(false)}
          projectId={projectId}
          onCreated={({ list }) => {
            setNewOpen(false);
            if (list?.id) {
              setActiveId(list.id);
            }
            loadLists();
          }}
        />
      )}
    </div>
  );
}


function Stat({ label, value, warn, accent }) {
  const cls = warn
    ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]"
    : "border-white/5 bg-white/[0.02] text-[#cdd5e3]";
  return (
    <div className={`rounded p-2 border ${cls}`} style={accent && !warn ? { color: accent } : undefined}>
      <div className="uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-base font-bold leading-tight">{value}</div>
    </div>
  );
}


function PickingRows({ items, onPick, onReturn, onDelete, onRefresh, linkedJCs, expandedHistoryId, onToggleHistory }) {
  // Group by section so the user reads in plumbing order
  const groups = {};
  items.forEach((it) => {
    const k = it.section || "(uncategorised)";
    (groups[k] = groups[k] || []).push(it);
  });
  const sectionNames = Object.keys(groups);

  if (items.length === 0) {
    return (
      <div className="text-xs text-[#647087] text-center py-3">
        No rows yet. Use the buttons above to pull from quote / planner, or add manually.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {sectionNames.map((sec) => (
        <div key={sec}>
          <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1">{sec}</div>
          {/* Iter 290zi — sticky column header bar (desktop only) using the
              SAME `picking-row-grid` template the rows use, so cells
              perfectly align with the header. */}
          <div className="hidden sm:grid items-center gap-2 px-3 py-1.5 text-[9px] uppercase tracking-wider text-[#647087] font-bold bg-white/[0.02] border border-white/5 border-b-0 rounded-t picking-row-grid">
            <span className="w-2 h-2" />
            <span className="text-center">Src</span>
            <span>SKU</span>
            <span>Descript</span>
            <span className="text-center">Req</span>
            <span className="text-center">Picked</span>
            <span className="text-center">Disp</span>
            <span className="text-center">Ret</span>
            <span className="text-center">Out</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="border border-white/5 rounded-b sm:rounded-t-none overflow-hidden divide-y divide-white/5">
            {groups[sec].map((it) => (
              <PickingRow
                key={it.id}
                item={it}
                onPick={onPick}
                onReturn={onReturn}
                onDelete={onDelete}
                onRefresh={onRefresh}
                linkedJCs={linkedJCs}
                isHistoryOpen={expandedHistoryId === it.id}
                onToggleHistory={() => onToggleHistory(it.id)}
                siblings={items}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}


function PickingRow({ item, onRefresh, isHistoryOpen, onToggleHistory, onDelete, linkedJCs = [], siblings = [] }) {
  // Iter 290zh — tap-to-edit qty cells (no more +N buttons). Each qty
  // column (Picked / Disp / Ret) opens an inline number input on click
  // that commits to /set-qty on blur or Enter, with the server logging
  // a movement for the delta. Camera + comment icons stay on the row.
  // Click the row body (anywhere outside an editable cell or icon) to
  // expand the photo strip / notes / history.
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null);          // 'picked' | 'dispatched' | 'returned' | null
  const [draft, setDraft] = useState("");
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [noteDraft, setNoteDraft] = useState(item.notes || "");
  const stat = ITEM_STATUS[item.status] || ITEM_STATUS.pending;
  const outstanding = item.qty_outstanding || 0;
  // Iter 400.9b — Variance signal. When the tech has returned some
  // stock (so net_used < picked), the Picked cell tints amber and the
  // tooltip surfaces the variance. Also drives the small "Net used on
  // site" line inside the unfolded row.
  const netUsed = Number.isFinite(item.qty_net_used) ? item.qty_net_used : Math.max(0, (item.qty_picked || 0) - (item.qty_returned || 0));
  const hasVariance = (item.qty_returned || 0) > 0 && netUsed < (item.qty_picked || 0);
  const photos = item.photos || [];
  const photoCount = photos.length;
  const hasNote = !!(item.notes && item.notes.trim());

  useEffect(() => { setNoteDraft(item.notes || ""); }, [item.notes]);

  // Iter 290zl — link-to-jobcard state. When a row is manually added,
  // it has no source_jobcard_id and therefore won't show in the JC's
  // materials[] list. This control lets the user push it across.
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [linking, setLinking] = useState(false);
  // Iter 411 — Manual merge picker state. Real world: OCR sometimes
  // reads the same physical product as two different SKUs (e.g.
  // AJM75S vs AJM75). The operator opens this picker to pick a
  // target row on the same list to absorb this row into.
  const [mergePickerOpen, setMergePickerOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState("");
  const [merging, setMerging] = useState(false);
  // Iter 411 — Manual split picker state. Real world: SO has one row
  // for "GENERAL FITTING PROVISION qty 100" — operator wants to split
  // that into e.g. 60 + 40 so two techs can pick sub-quantities.
  const [splitPickerOpen, setSplitPickerOpen] = useState(false);
  const [splitDrafts, setSplitDrafts] = useState([]);
  const [splitting, setSplitting] = useState(false);
  const linkedJC = item.source_jobcard_id
    ? (linkedJCs.find((jc) => jc.id === item.source_jobcard_id)
        || { id: item.source_jobcard_id, title: "(job card)", short_id: "" })
    : null;

  const linkToJC = async (jid) => {
    if (!jid) return;
    setLinking(true);
    try {
      await api.post(`/picking/items/${item.id}/link-jobcard`, { jobcard_id: jid });
      toast.success(linkedJC ? "Row reassigned." : "Row pushed onto the job card's materials list.");
      setLinkPickerOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed.");
    } finally {
      setLinking(false);
    }
  };

  const unlinkFromJC = async () => {
    setLinking(true);
    try {
      await api.post(`/picking/items/${item.id}/unlink-jobcard`);
      toast.success("Row unassigned from job card.");
      setLinkPickerOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unassign failed.");
    } finally {
      setLinking(false);
    }
  };

  // Iter 411 — Manual merge: absorb this row into a target row on
  // the same list. Fixes OCR-caused duplicate rows (AJM75S vs AJM75
  // etc.) that the auto-consolidator can't collapse because the
  // inventory-matcher pointed them at different inventory records.
  const mergeInto = async (targetId) => {
    if (!targetId || merging) return;
    setMerging(true);
    try {
      await api.post(`/picking/items/${item.id}/merge-into`, { target_id: targetId });
      toast.success("Row merged. Quantities and audit history preserved.");
      setMergePickerOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Merge failed.");
    } finally {
      setMerging(false);
    }
  };

  // Iter 411 — Open the splitter with a default 50/50 split.
  const openSplitPicker = () => {
    const source = Number(item.qty_required) || 0;
    const half = Math.floor(source / 2);
    const rest = source - half;
    setSplitDrafts([String(half || Math.floor(source * 0.5)), String(rest || source - Math.floor(source * 0.5))]);
    setSplitPickerOpen(true);
  };
  const performSplit = async () => {
    if (splitting) return;
    const nums = splitDrafts.map((s) => Number(String(s).replace(",", ".")) || 0);
    const total = nums.reduce((a, b) => a + b, 0);
    const source = Number(item.qty_required) || 0;
    if (nums.some((n) => n <= 0)) {
      toast.error("Every split must be greater than 0.");
      return;
    }
    if (Math.abs(total - source) > 0.01) {
      toast.error(`Splits must sum to ${source}. Currently ${total}.`);
      return;
    }
    setSplitting(true);
    try {
      await api.post(`/picking/items/${item.id}/split`, { splits: nums });
      toast.success(`Row split into ${nums.length} rows.`);
      setSplitPickerOpen(false);
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Split failed.");
    } finally {
      setSplitting(false);
    }
  };

  const ORIGIN = {
    jobcard:    { l: "JC",    c: "#1FB6C1" },
    quote:      { l: "QUOTE", c: "#A78BFA" },
    sales_doc:  { l: "SO",    c: "#10B981" },
    sales_order:{ l: "SO",    c: "#10B981" },
    planner:    { l: "PLAN",  c: "#F59E0B" },
    manual:     { l: "MAN",   c: "#647087" },
  };
  const origin = ORIGIN[item.source || "manual"] || ORIGIN.manual;

  const commitQty = async (field, value) => {
    setEditing(null);
    const v = parseFloat(value);
    if (Number.isNaN(v) || v < 0) { setDraft(""); return; }
    const current = {
      picked: item.qty_picked || 0,
      dispatched: item.qty_dispatched || 0,
      returned: item.qty_returned || 0,
      // Iter 290zp.5 — Site Used is the on-site recon count; no
      // inventory delta, just a stored value. Backend computes the
      // Diff vs Auto Used (dispatched − returned).
      site_used: item.qty_site_used ?? 0,
      // Iter 418.j (Dirk, 2026-02-28) — allow required qty edits on
      // manually-added rows. See required-cell render below for the
      // source==='manual' gate.
      required: item.qty_required || 0,
    }[field];
    if (Math.abs(v - current) < 0.0001) return;
    try {
      // Iter 418.j — required uses the generic PATCH endpoint (no
      // inventory delta, no set-qty helper). Everything else keeps
      // hitting /set-qty which handles over-pick guards + movement
      // logging.
      if (field === "required") {
        await api.patch(`/picking/items/${item.id}`, { qty_required: v });
        toast.success(`Required = ${v}`);
        onRefresh?.();
        return;
      }
      await api.post(`/picking/items/${item.id}/set-qty`, { field, value: v });
      const lbl = {
        picked: "Picked", dispatched: "Dispatched", returned: "Returned",
        site_used: "Site used",
      }[field] || field;
      toast.success(`${lbl} = ${v}`);
      onRefresh?.();
    } catch (e) {
      // Over-pick / over-return guard — confirm and retry with force=true
      const detail = e?.response?.data?.detail || "";
      const isForcePrompt = typeof detail === "string" && (
        detail.startsWith("Picked > required") ||
        detail.startsWith("Returned > picked")
      );
      if (isForcePrompt) {
        if (window.confirm(`${detail}\n\nConfirm anyway?`)) {
          try {
            await api.post(`/picking/items/${item.id}/set-qty`, { field, value: v, force: true });
            toast.success(`${field} = ${v} (forced).`);
            onRefresh?.();
            return;
          } catch (err2) {
            toast.error(err2?.response?.data?.detail || "Update failed.");
          }
        }
      } else {
        toast.error(detail || "Update failed.");
      }
    }
  };

  const saveNote = async () => {
    if (noteDraft === (item.notes || "")) return;
    try {
      await api.patch(`/picking/items/${item.id}`, { notes: noteDraft });
      onRefresh?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    }
  };

  // Editable qty cell — tap to edit, blur/Enter to commit, Esc to cancel.
  // Iter 398h — Dirk reported on prod (build 92702029) that picked /
  // dispatched / returned couldn't be edited on tablet/iPad PWA — only
  // Site Used worked. Root cause: the parent row is a `role="button"`
  // with `onClick={toggle expand}`, and on iPad Safari a plain
  // `e.stopPropagation()` inside onClick sometimes fires AFTER the
  // parent's touch handler has already swallowed the tap. Fix: also
  // stop propagation + prevent default on the earlier events
  // (`onPointerDown`, `onMouseDown`, `onTouchStart`) so the click never
  // reaches the row toggle. Site Used already worked because it lives
  // inside the expanded panel (not wrapped in the row-toggle button).
  const QtyCell = ({ field, value, color, width = "w-12", title }) => {
    const isEditing = editing === field;
    if (isEditing) {
      return (
        <input
          type="number" min="0" step="any"
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => commitQty(field, draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); e.target.blur(); }
            if (e.key === "Escape") { setEditing(null); setDraft(""); }
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          className={`hv-input ${width} text-[11px] font-mono text-center py-0 px-1 h-6`}
          data-testid={`picking-cell-edit-${field}-${item.id}`}
        />
      );
    }
    const stopAll = (e) => e.stopPropagation();
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setEditing(field);
          setDraft(String(value ?? ""));
        }}
        onPointerDown={stopAll}
        onMouseDown={stopAll}
        onTouchStart={stopAll}
        title={title}
        className={`${width} text-[11px] font-mono text-center px-1 py-0.5 rounded hover:bg-white/[0.06] border border-transparent hover:border-white/10 ${color}`}
        data-testid={`picking-cell-${field}-${item.id}`}
      >
        {fmtN(value)}
      </button>
    );
  };

  // Iter 418.j (Dirk, 2026-02-28) — soft-deleted rows render at the
  // bottom in red with a Restore button. Backend already sorts deleted
  // rows last in GET /picking/lists/{lid}.
  const isDeleted = !!item.deleted_at;
  const restoreItem = async () => {
    try {
      await api.post(`/picking/items/${item.id}/restore`, {});
      toast.success("Row restored.");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed.");
    }
  };

  return (
    <div
      data-testid={`picking-row-${item.id}`}
      className={isDeleted ? "opacity-70 bg-rose-500/[0.06] border-l-2 border-rose-500/40" : ""}
    >
      {isDeleted && (
        <div className="px-3 py-1 flex items-center gap-2 flex-wrap text-[10px] uppercase tracking-wider">
          <span className="text-rose-300 font-bold">Removed</span>
          {item.deleted_by_name && (
            <span className="text-rose-300/70 normal-case">
              by {item.deleted_by_name} · {(item.deleted_at || "").slice(0, 16).replace("T", " ")}
            </span>
          )}
          <button
            type="button"
            onClick={restoreItem}
            className="hv-btn hv-btn-ghost text-[10px] py-0.5 border-emerald-400/40 text-emerald-300 hover:text-emerald-200"
            data-testid={`picking-row-restore-${item.id}`}
            title="Restore this row to the active picking list"
          >
            ↺ Restore
          </button>
        </div>
      )}
      {/* DENSE COLLAPSED ROW — desktop: single grid-aligned row;
          mobile: 2 lines (SRC + SKU + name above, qty pills + icons below).
          Iter 398h — the collapsed row IS the click target for toggling
          expand/collapse. QtyCells + action buttons live INSIDE it on the
          desktop grid, so we early-return here when the click's actual
          target sits inside an interactive element (button, input, or an
          element explicitly opted-out via `data-noexpand`). Without this,
          the parent handler fires AFTER the child's `e.stopPropagation()`
          in some Safari flavours because React uses synthetic-event
          bubbling that doesn't fully bypass every native path. */}
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          const t = e.target;
          if (t.closest?.("button, input, [data-noexpand], a, textarea, select, label")) return;
          setExpanded((v) => !v);
        }}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded((v) => !v); } }}
        className="px-3 py-1 sm:py-2 text-xs hover:bg-white/[0.03] cursor-pointer select-none"
        data-testid={`picking-row-head-${item.id}`}
      >
        {/* DESKTOP: one row grid aligned with the header strip */}
        <div className="hidden sm:grid items-center gap-2 picking-row-grid">
          <span className="w-2 h-2 rounded-full justify-self-center" style={{ background: stat.c }} title={stat.l} />
          <span
            className="text-[9px] uppercase font-mono px-1 py-0.5 rounded text-center"
            style={{ background: `${origin.c}22`, color: origin.c, border: `1px solid ${origin.c}55` }}
            title={`Origin: ${item.source || "manual"}`}
          >
            {origin.l}
          </span>
          <span className="text-[10px] font-mono text-[#a0aabf] truncate" title={item.sku || ""}>
            {item.sku || (item.is_non_stock ? "non-stock" : "—")}
          </span>
          <div className="min-w-0">
            <span className="text-white truncate block leading-tight">{item.name}</span>
            {mergedCount(item) > 1 && (
              <span className="text-[9px] text-amber-200 block leading-tight" data-testid={`picking-row-merged-hint-${item.id}`}>merged from {mergedCount(item)} lines · tap to view</span>
            )}
            {(item.due_date || (typeof item.qty_on_hand === "number" && !item.is_non_stock)) && (
              <span className="text-[9px] text-[#647087] truncate block leading-tight">
                {item.due_date && <span className="text-[#F59E0B]">due {item.due_date} · </span>}
                {typeof item.qty_on_hand === "number" && !item.is_non_stock && (
                  <span className={item.qty_on_hand < outstanding ? "text-rose-300" : "text-cyan-300"}>
                    OH {item.qty_on_hand}
                  </span>
                )}
              </span>
            )}
          </div>
          {/* Iter 418.j — required qty is editable for manually-added
              rows (source === "manual"). SO/quote-imported rows stay
              read-only so an accidental tap can't drift the source-of-
              truth quantity away from the sales document. */}
          {(item.source === "manual" || !item.source) && !isDeleted ? (
            <QtyCell
              field="required"
              value={item.qty_required || 0}
              color="text-[#a0aabf]"
              width="w-full"
              title="Required qty — tap to edit (manual row)"
            />
          ) : (
            <span className="text-[11px] font-mono text-center text-[#a0aabf]" title={item.source === "manual" ? "Required" : "Required (locked — imported from sales doc)"}>
              {fmtN(item.qty_required)}
            </span>
          )}
          {expanded ? (
            <>
              {/* Iter 398h.2 — When the row is expanded, the editable
                  QtyCells live in the expanded panel below. Rendering
                  editable QtyCells here too causes duplicate <input
                  autoFocus> nodes that steal focus from each other via
                  onBlur → commitQty → setEditing(null), cancelling the
                  edit. We render read-only spans instead so exactly ONE
                  editable instance per field exists at any time. */}
              <span
                className={`text-[11px] font-mono text-center opacity-70 ${hasVariance ? "text-amber-300" : "text-emerald-300"}`}
                title={hasVariance ? `Picked ${item.qty_picked} · Net used ${netUsed} (returned ${item.qty_returned || 0}) — edit in panel below` : "Picked (edit in panel below)"}
              >{fmtN(item.qty_picked || 0)}</span>
              <span className="text-[11px] font-mono text-center text-sky-300 opacity-70"     title="Dispatched (edit in panel below)">{fmtN(item.qty_dispatched || 0)}</span>
              <span className="text-[11px] font-mono text-center text-violet-300 opacity-70"  title="Returned (edit in panel below)">{fmtN(item.qty_returned || 0)}</span>
            </>
          ) : (
            <>
              <QtyCell
                field="picked"
                value={item.qty_picked || 0}
                color={hasVariance ? "text-amber-300" : "text-emerald-300"}
                width="w-full"
                title={
                  hasVariance
                    ? `Picked ${item.qty_picked} · Net used on site ${netUsed} (returned ${item.qty_returned || 0}) — tap to edit`
                    : "Picked — tap to edit"
                }
              />
              <QtyCell field="dispatched" value={item.qty_dispatched || 0} color="text-sky-300"     width="w-full" title="Dispatched — tap to edit" />
              <QtyCell field="returned"   value={item.qty_returned || 0}   color="text-violet-300"  width="w-full" title="Returned — tap to edit" />
            </>
          )}
          <span className={`text-[11px] font-mono text-center ${outstanding > 0 ? "text-amber-300" : "text-[#647087]"}`} title="Outstanding">{fmtN(outstanding)}</span>
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <PhotoBtn count={photoCount} onClick={() => setExpanded(true)} testid={`picking-photos-btn-${item.id}`} />
            <CommentBtn hasNote={hasNote} note={item.notes} onClick={() => setExpanded(true)} testid={`picking-comment-btn-${item.id}`} />
            {expanded ? <ChevronDown className="w-3.5 h-3.5 text-[#647087]" /> : <ChevronRight className="w-3.5 h-3.5 text-[#647087]" />}
          </div>
        </div>

        {/* MOBILE: dense single-line row — picker sees 16+ items per
            screen and scrolls to find what's in his hand. Tap row to
            unfold the inline editor (qty + comment + photo upload).
            Iter 290zj — rewritten after user feedback: previous 2-line
            layout only fit ~6 rows per screen, useless for picking 300
            tiny fittings off shelves. Reference pattern: Cin7 Core /
            Unleashed / Odoo mobile pickers (dense list + bottom-sheet). */}
        <div className="sm:hidden flex items-center gap-2 min-h-[36px]">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stat.c }} title={stat.l} />
          <span className="text-[13px] text-white truncate flex-1 min-w-0 leading-tight" title={item.name}>
            {item.name}
            {item.is_non_stock && (
              <span className="ml-1 text-[9px] font-mono uppercase text-[#A78BFA]">·{(item.non_stock_kind || "non-stock").toUpperCase()}</span>
            )}
          </span>
          {photoCount > 0 && (
            <span
              role="img"
              aria-label={`${photoCount} photo${photoCount === 1 ? "" : "s"}`}
              className="inline-flex items-center text-[10px] font-mono text-[#a0aabf] shrink-0"
              data-testid={`picking-photo-indicator-${item.id}`}
            >
              <Camera className="w-3 h-3 mr-0.5" />{photoCount}
            </span>
          )}
          {hasNote && (
            <PenLine
              className="w-3 h-3 text-amber-300 shrink-0"
              aria-label="Has comment"
              data-testid={`picking-note-indicator-${item.id}`}
            />
          )}
          <MobileQtyTally
            picked={item.qty_picked || 0}
            required={item.qty_required || 0}
          />
          {expanded
            ? <ChevronDown className="w-3.5 h-3.5 text-[#647087] shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 text-[#647087] shrink-0" />}
        </div>
      </div>

      {/* EXPANDED — full description + 4-col qty grid + notes + photo strip */}
      {expanded && (
        <div className="px-3 pb-3 pt-2 space-y-2 bg-white/[0.015] border-t border-white/5" data-testid={`picking-row-expanded-${item.id}`}>
          {/* Iter 290zk — full untruncated item description at top of the
              expand panel. The collapsed row truncates to keep density at
              16+ rows/screen; here we surface the complete name + SKU +
              optional description for verification. */}
          <div className="text-sm text-white leading-snug font-medium break-words flex items-start gap-1.5" data-testid={`picking-row-fullname-${item.id}`}>
            <span className="flex-1">{item.name}</span>
            <EditLineButton item={item} url={`/picking/items/${item.id}`} onDone={onRefresh} testPrefix="picking-item" />
          </div>
          <MergedLinesPanel item={item} baseUrl={`/picking/items/${item.id}`} onDone={onRefresh} testPrefix="picking-item" />
          {(item.sku || item.description) && (
            <div className="text-[10px] text-[#a0aabf] font-mono break-words">
              {item.sku && <span className="text-[#cdd5e3]">{item.sku}</span>}
              {item.sku && item.description && <span className="text-[#647087]"> · </span>}
              {item.description}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
            <span className="font-mono uppercase px-1.5 py-0.5 rounded" style={{ background: `${stat.c}1f`, color: stat.c }}>{stat.l}</span>
            {item.is_non_stock && (
              <span className="uppercase font-mono px-1.5 py-0.5 rounded" style={{ background: "#A78BFA1f", color: "#A78BFA" }}>
                {(item.non_stock_kind || "NON-STOCK").toUpperCase().replace(/_/g, " ")}
              </span>
            )}
            <div className="flex-1" />
            <button onClick={onToggleHistory} className={`text-[10px] flex items-center gap-1 ${isHistoryOpen ? "text-[#1FB6C1]" : "text-[#a0aabf] hover:text-white"}`} data-testid={`picking-history-${item.id}`}>
              <History className="w-3 h-3" /> {isHistoryOpen ? "Hide history" : "History"}
            </button>
            {/* Iter 411 — Manual merge trigger. Only shown when there are
                other live rows on the list to merge with, and when this
                row itself hasn't been dispatched yet. */}
            {siblings.filter((s) => s.id !== item.id && !s.deleted_at && (s.status || "") !== "dispatched").length > 0
              && (item.status || "") !== "dispatched" && (
              <button
                onClick={() => setMergePickerOpen((v) => !v)}
                className={`text-[10px] flex items-center gap-1 ${mergePickerOpen ? "text-[#1FB6C1]" : "text-[#a0aabf] hover:text-white"}`}
                title="Merge this row into another row on the same list"
                data-testid={`picking-merge-${item.id}`}
              >
                <GitMerge className="w-3 h-3" /> Merge
              </button>
            )}
            {/* Iter 411 — Manual split trigger. Splits qty across N
                rows (e.g. 100 → 60+40). Only when this row has qty
                to split (> 1) and isn't dispatched. */}
            {Number(item.qty_required) > 1 && (item.status || "") !== "dispatched" && (
              <button
                onClick={() => (splitPickerOpen ? setSplitPickerOpen(false) : openSplitPicker())}
                className={`text-[10px] flex items-center gap-1 ${splitPickerOpen ? "text-[#1FB6C1]" : "text-[#a0aabf] hover:text-white"}`}
                title="Split this row's qty across multiple rows (must sum to original)"
                data-testid={`picking-split-${item.id}`}
              >
                <GitBranch className="w-3 h-3" /> Split
              </button>
            )}
            <button onClick={() => onDelete(item.id)} className="text-rose-400 hover:text-rose-300 text-[10px]" data-testid={`picking-delete-${item.id}`}>
              Remove
            </button>
          </div>

          {/* Iter 411 — Merge picker (inline expand). Shows every other
              live row on the list, ranked by SKU / name similarity so
              the operator can quickly spot the near-match target for
              this row. */}
          {mergePickerOpen && (
            <MergePicker
              item={item}
              siblings={siblings}
              query={mergeQuery}
              onQuery={setMergeQuery}
              onCancel={() => { setMergePickerOpen(false); setMergeQuery(""); }}
              onPick={mergeInto}
              busy={merging}
            />
          )}

          {/* Iter 411 — Split picker (inline expand). Enforces sum-to-
              source so stock is never invented or lost. */}
          {splitPickerOpen && (
            <SplitPicker
              item={item}
              splits={splitDrafts}
              setSplits={setSplitDrafts}
              onCancel={() => setSplitPickerOpen(false)}
              onSubmit={performSplit}
              busy={splitting}
            />
          )}

          {/* Iter 290zm — link-to-job-card with reassignment.
              The chip is now a dropdown: tap to reassign (across all
              JCs linked to the list) or unassign. */}
          {linkedJCs.length > 0 ? (
            <div data-testid={`picking-row-jc-link-${item.id}`}>
              {!linkPickerOpen ? (
                linkedJC ? (
                  <button
                    type="button"
                    onClick={() => setLinkPickerOpen(true)}
                    className="flex items-center gap-2 text-[11px] rounded border border-emerald-400/30 bg-emerald-500/[0.06] px-2 py-1 w-full hover:bg-emerald-500/[0.12]"
                    data-testid={`picking-row-jc-linked-${item.id}`}
                    title="Tap to reassign or unassign"
                  >
                    <Link2 className="w-3 h-3 text-emerald-300 shrink-0" />
                    <span className="text-emerald-200 truncate flex-1 text-left">
                      Linked to job card · <span className="text-white font-medium">{linkedJC.title || "(no title)"}</span>
                      {linkedJC.short_id && <span className="text-[#a0aabf] font-mono"> · {linkedJC.short_id}</span>}
                    </span>
                    <ChevronDown className="w-3 h-3 text-emerald-300/70 shrink-0" />
                  </button>
                ) : linkedJCs.length === 1 ? (
                  <button
                    type="button"
                    onClick={() => linkToJC(linkedJCs[0].id)}
                    disabled={linking}
                    className="hv-btn hv-btn-ghost text-[11px] w-full justify-start disabled:opacity-50"
                    data-testid={`picking-row-jc-link-btn-${item.id}`}
                  >
                    {linking
                      ? <Loader2 className="w-3 h-3 animate-spin" />
                      : <Link2 className="w-3 h-3 text-cyan-300" />}
                    <span className="truncate">
                      Link to job card · <span className="text-white">{linkedJCs[0].title || linkedJCs[0].short_id || "JC"}</span>
                    </span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setLinkPickerOpen(true)}
                    className="hv-btn hv-btn-ghost text-[11px] w-full justify-start"
                    data-testid={`picking-row-jc-link-open-${item.id}`}
                  >
                    <Link2 className="w-3 h-3 text-cyan-300" />
                    Link to job card ({linkedJCs.length} options)…
                  </button>
                )
              ) : (
                <div className="rounded border border-cyan-400/30 bg-cyan-500/[0.06] p-2 space-y-1" data-testid={`picking-row-jc-picker-${item.id}`}>
                  <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold flex items-center justify-between">
                    <span>{linkedJC ? "Reassign / unassign" : "Pick a job card"}</span>
                    <button
                      type="button"
                      onClick={() => setLinkPickerOpen(false)}
                      className="text-[#647087] hover:text-white"
                      data-testid={`picking-row-jc-picker-close-${item.id}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  {linkedJCs.map((jc) => {
                    const isCurrent = linkedJC && jc.id === linkedJC.id;
                    return (
                      <button
                        key={jc.id}
                        type="button"
                        onClick={() => !isCurrent && linkToJC(jc.id)}
                        disabled={linking || isCurrent}
                        className={`w-full text-left px-2 py-1.5 rounded border text-[11px] flex items-center gap-2 disabled:opacity-50 ${
                          isCurrent
                            ? "border-emerald-400/40 bg-emerald-500/[0.10] cursor-default"
                            : "border-white/5 bg-[#0a0b0e] hover:bg-white/[0.04]"
                        }`}
                        data-testid={`picking-row-jc-pick-${item.id}-${jc.id}`}
                      >
                        {isCurrent
                          ? <Check className="w-3 h-3 text-emerald-300 shrink-0" />
                          : <Link2 className="w-3 h-3 text-cyan-300 shrink-0" />}
                        <span className="text-white truncate flex-1">{jc.title || "(untitled)"}</span>
                        {jc.short_id && <span className="text-[10px] font-mono text-[#a0aabf] shrink-0">{jc.short_id}</span>}
                      </button>
                    );
                  })}
                  {linkedJC && (
                    <button
                      type="button"
                      onClick={unlinkFromJC}
                      disabled={linking}
                      className="w-full text-left px-2 py-1.5 rounded border border-rose-400/30 bg-rose-500/[0.06] hover:bg-rose-500/[0.12] text-[11px] flex items-center gap-2 text-rose-200 disabled:opacity-50"
                      data-testid={`picking-row-jc-unassign-${item.id}`}
                    >
                      <X className="w-3 h-3 shrink-0" />
                      Unassign from this job card
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="text-[10px] text-[#647087] italic" data-testid={`picking-row-jc-none-${item.id}`}>
              No job card linked to this picking list yet — link one from the header above to push rows back to it.
            </div>
          )}

          {/* Picked / Disp / Ret / Out — 4 qty fields side-by-side.
              Iter 398h — user asked to also expose these editable in the
              expanded view on iPad/desktop (previously only Site Used was
              editable when expanded on larger screens). We drop `sm:hidden`
              so this grid renders at ALL breakpoints in the expanded panel.
              Inline style sidesteps Tailwind purge of grid-cols-4. */}
          <div
            className="gap-1.5 text-[10px]"
            style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
            data-testid={`picking-qty-grid-${item.id}`}
          >
            <div className="text-center">
              <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Picked</div>
              <QtyCell
                field="picked"
                value={item.qty_picked || 0}
                color={`${hasVariance ? "text-amber-300" : "text-emerald-300"} bg-emerald-500/[0.08] border border-emerald-400/20 block w-full`}
                width="w-full"
                title={hasVariance ? `Net used ${netUsed} of ${item.qty_picked} picked` : "Picked"}
              />
            </div>
            <div className="text-center">
              <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Disp</div>
              <QtyCell field="dispatched" value={item.qty_dispatched || 0} color="text-sky-300 bg-sky-500/[0.08] border border-sky-400/20 block w-full" width="w-full" title="Dispatched" />
            </div>
            <div className="text-center">
              <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Ret</div>
              <QtyCell field="returned" value={item.qty_returned || 0} color="text-violet-300 bg-violet-500/[0.08] border border-violet-400/20 block w-full" width="w-full" title="Returned" />
            </div>
            <div className="text-center">
              <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Out</div>
              <div className={`text-[12px] font-mono py-1 rounded border ${outstanding > 0 ? "text-amber-300 bg-amber-500/[0.08] border-amber-400/20" : "text-[#647087] bg-white/[0.02] border-white/10"}`}>{fmtN(outstanding)}</div>
            </div>
          </div>
          {/* OH stock hint (mobile expanded) — moved here from row so the
              collapsed row stays one line. */}
          {(typeof item.qty_on_hand === "number" && !item.is_non_stock) && (
            <div className="sm:hidden text-[10px] font-mono">
              <span className={item.qty_on_hand < outstanding ? "text-rose-300" : "text-cyan-300"}>
                OH {item.qty_on_hand}
                {item.qty_on_hand < outstanding && " · short → procurement"}
              </span>
            </div>
          )}
          {/* Iter 400.9b — Net used variance line. Shown ONLY when a
              return has taken place so the compact view stays clean
              during normal picking. Deliberately below the qty grid
              and above the recon strip so the manager reads
              "planned → picked → net used" naturally. */}
          {hasVariance && (
            <div
              className="rounded border border-amber-500/25 bg-amber-500/[0.06] px-2 py-1 text-[10px] flex items-center justify-between"
              data-testid={`picking-net-used-${item.id}`}
            >
              <span className="text-amber-300 font-mono uppercase tracking-wider">
                Net used on site
              </span>
              <span className="font-mono text-amber-200">
                <span className="font-bold">{fmtN(netUsed)}</span>
                <span className="text-amber-300/70"> of {fmtN(item.qty_picked || 0)} picked · returned {fmtN(item.qty_returned || 0)}</span>
              </span>
            </div>
          )}
          {/* Auto Used · Site Used · Diff — the "reconciliation" strip.
              Matches the right-hand columns on the printed picking slip
              so the printout and the live row always read the same.
                Auto Used = Dispatched − Returned (server-computed)
                Site Used = on-site recon count (tap to edit)
                Diff      = Site Used − Auto Used (server-computed,
                            <0 = loss / shrinkage / theft (RED),
                            >0 = used more than dispatched — off-spec
                                 or untracked source (AMBER)). */}
          {(() => {
            const auto = item.qty_auto_used ?? 0;
            const site = item.qty_site_used;
            const diff = item.qty_diff;
            const hasSite = site !== null && site !== undefined && site !== "";
            const diffColor = !hasSite
              ? "text-[#647087] bg-white/[0.02] border-white/10"
              : Math.abs(diff || 0) < 0.0001
                ? "text-emerald-300 bg-emerald-500/[0.08] border-emerald-400/20"
                : (diff || 0) < 0
                  ? "text-rose-300 bg-rose-500/[0.08] border-rose-400/20"
                  : "text-amber-300 bg-amber-500/[0.08] border-amber-400/20";
            return (
              <div
                className="gap-1.5 text-[10px]"
                style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}
                data-testid={`picking-recon-grid-${item.id}`}
              >
                <div className="text-center" title="Auto Used = Dispatched − Returned (server-computed)">
                  <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Auto used</div>
                  <div className="text-[12px] font-mono py-1 rounded border text-cyan-300 bg-cyan-500/[0.08] border-cyan-400/20">{fmtN(auto)}</div>
                </div>
                <div className="text-center" title="Site Used — on-site recon count. Tap to edit.">
                  <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Site used</div>
                  <QtyCell
                    field="site_used"
                    value={hasSite ? site : ""}
                    color="text-teal-200 bg-teal-500/[0.08] border border-teal-400/20 block w-full"
                    width="w-full"
                    title="Site Used — tap to edit"
                  />
                </div>
                <div className="text-center" title={!hasSite ? "Diff appears once Site Used is captured" : (diff < 0 ? "Site used less than dispatched — possible loss / shrinkage" : diff > 0 ? "Site used more than dispatched — off-spec / extra source" : "No discrepancy")}>
                  <div className="uppercase tracking-wider text-[#647087] mb-0.5 leading-none">Diff</div>
                  <div className={`text-[12px] font-mono py-1 rounded border ${diffColor}`}>
                    {hasSite ? ((diff > 0 ? "+" : "") + fmtN(diff)) : "—"}
                  </div>
                </div>
              </div>
            );
          })()}
          {/* Notes editor */}
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] uppercase tracking-wider text-[#647087] shrink-0">Note</span>
            <input
              type="text"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onBlur={saveNote}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNote(); e.target.blur(); } }}
              className="hv-input flex-1 text-[11px] py-1"
              placeholder="Add a comment…"
              data-testid={`picking-note-input-${item.id}`}
            />
          </div>
          <PhotoStrip item={item} photos={photos} onUploaded={onRefresh} onDeleted={onRefresh} onOpen={(p) => setLightboxPhoto(p)} />
          {isHistoryOpen && <InlineMovementHistory itemId={item.id} />}
          {lightboxPhoto && <InlinePhotoLightbox photo={lightboxPhoto} onClose={() => setLightboxPhoto(null)} />}
        </div>
      )}
    </div>
  );
}

function fmtN(n) {
  const v = Number(n || 0);
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(2).replace(/\.?0+$/, "");
}

/** Photo + count badge button (shared desktop/mobile) */
function PhotoBtn({ count, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative text-[#a0aabf] hover:text-white p-1 shrink-0"
      title={`${count} photo${count === 1 ? "" : "s"}`}
      data-testid={testid}
    >
      <Camera className="w-4 h-4" />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 bg-cyan-500 text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center text-white">{count}</span>
      )}
    </button>
  );
}

function CommentBtn({ hasNote, note, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative p-1 shrink-0 ${hasNote ? "text-amber-300" : "text-[#a0aabf] hover:text-white"}`}
      title={hasNote ? note : "Add comment"}
      data-testid={testid}
    >
      <FileText className="w-4 h-4" />
      {hasNote && <span className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-amber-300 rounded-full" />}
    </button>
  );
}

/** Mobile dense single-line picked/required tally — color-coded by state.
 *  Iter 290zj. Replaces the old MobileQtyPill + MobileEditPill stack which
 *  forced the mobile row into a 2-line layout (only 6 visible per screen).
 *  Pickers grab whatever's in front of them; they need a long scrollable
 *  list. Tap the row to unfold the editor; this tally is read-only on the
 *  collapsed row. */
function MobileQtyTally({ picked, required }) {
  const p = Number(picked || 0);
  const r = Number(required || 0);
  let cls = "text-[#647087]";   // required==0 → muted
  let strike = "";
  if (r > 0) {
    if (p >= r) { cls = "text-emerald-300"; strike = "opacity-60"; }
    else if (p > 0) cls = "text-amber-300";
    else cls = "text-rose-300";
  }
  return (
    <span
      className={`text-[13px] font-mono font-bold whitespace-nowrap shrink-0 tabular-nums ${cls} ${strike}`}
      title={`Picked ${fmtN(p)} of ${fmtN(r)} required`}
    >
      {fmtN(p)}<span className="opacity-50">/</span>{fmtN(r)}
    </span>
  );
}



/**
 * MergePicker — Iter 411.
 *
 * Inline picker used to manually merge a picking row into another row
 * on the same list. Auto-ranks candidates by SKU / name similarity so
 * the OCR-caused near-duplicate (e.g. `AJM75S(220V)` alongside
 * `AJM75(220V)`) always floats to the top.
 */
function MergePicker({ item, siblings, query, onQuery, onCancel, onPick, busy }) {
  const candidates = (siblings || []).filter(
    (s) => s.id !== item.id && !s.deleted_at && (s.status || "") !== "dispatched",
  );

  const scoreOf = (c) => {
    const mySku = (item.sku || "").toUpperCase();
    const otherSku = (c.sku || "").toUpperCase();
    const myName = (item.name || "").toLowerCase();
    const otherName = (c.name || "").toLowerCase();
    let s = 0;
    if (item.inventory_id && c.inventory_id && item.inventory_id === c.inventory_id) s += 100;
    if (mySku && otherSku) {
      if (mySku === otherSku) s += 60;
      else if (mySku.startsWith(otherSku) || otherSku.startsWith(mySku)) s += 35;
      else if (mySku.replace(/[^A-Z0-9]/g, "") === otherSku.replace(/[^A-Z0-9]/g, "")) s += 45;
      else {
        let common = 0;
        for (let i = 0; i < Math.min(mySku.length, otherSku.length); i++) {
          if (mySku[i] !== otherSku[i]) break;
          common++;
        }
        if (common >= 4) s += Math.min(25, common * 3);
      }
    }
    if (myName && otherName) {
      if (myName === otherName) s += 40;
      else {
        const t1 = new Set(myName.split(/\s+/).filter((w) => w.length > 2));
        const t2 = new Set(otherName.split(/\s+/).filter((w) => w.length > 2));
        if (t1.size && t2.size) {
          let inter = 0;
          t1.forEach((w) => { if (t2.has(w)) inter++; });
          s += Math.round((inter / Math.max(t1.size, t2.size)) * 20);
        }
      }
    }
    return s;
  };

  const q = (query || "").trim().toLowerCase();
  const filtered = candidates
    .map((c) => ({ c, s: scoreOf(c) }))
    .filter(({ c }) => {
      if (!q) return true;
      return (
        (c.sku || "").toLowerCase().includes(q)
        || (c.name || "").toLowerCase().includes(q)
      );
    })
    .sort((a, b) => b.s - a.s)
    .slice(0, 25);

  return (
    <div
      className="mt-2 border border-[#1FB6C1]/40 rounded bg-[#1FB6C1]/[0.06] p-2 space-y-2"
      data-testid={`picking-merge-picker-${item.id}`}
    >
      <div className="flex items-center gap-2">
        <GitMerge className="w-3.5 h-3.5 text-[#1FB6C1]" />
        <div className="text-[11px] flex-1">
          <div className="text-white font-medium">
            Merge <span className="font-mono text-[#a0aabf]">{item.sku || "(no SKU)"}</span> · qty {item.qty_required} into…
          </div>
          <div className="text-[10px] text-[#647087]">
            Adds this row&apos;s qty into the target. This row is soft-deleted with a full audit trail.
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="hv-btn hv-btn-ghost text-[10px]"
          data-testid={`picking-merge-cancel-${item.id}`}
        >
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
      <div className="relative">
        <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-[#647087]" />
        <input
          type="text"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Filter candidates by SKU or name"
          className="w-full bg-black/40 border border-white/10 rounded pl-7 pr-2 py-1 text-[11px] text-white placeholder:text-[#647087] focus:border-[#1FB6C1]/60 focus:outline-none"
          data-testid={`picking-merge-filter-${item.id}`}
          autoFocus
        />
      </div>
      <div className="max-h-56 overflow-y-auto space-y-1">
        {filtered.length === 0 ? (
          <div className="text-[10px] text-[#647087] text-center py-3">
            No candidates on this list match your filter.
          </div>
        ) : (
          filtered.map(({ c, s }) => (
            <button
              key={c.id}
              type="button"
              disabled={busy}
              onClick={() => onPick(c.id)}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded border border-white/5 bg-black/30 hover:border-[#1FB6C1]/40 hover:bg-[#1FB6C1]/[0.08] transition-colors disabled:opacity-50"
              data-testid={`picking-merge-target-${c.id}`}
              title={s > 0 ? `Match confidence: ${s}` : "Low confidence — double-check before merging"}
            >
              <div className="min-w-0 flex-1">
                <div className="text-[11px] text-white truncate flex items-center gap-1.5">
                  <span className="font-mono text-[#a0aabf]">{c.sku || "(no SKU)"}</span>
                  <span className="truncate">{c.name || "(no name)"}</span>
                </div>
                <div className="text-[9px] text-[#647087] font-mono">
                  qty {c.qty_required} · picked {c.qty_picked || 0} · outstanding {c.qty_outstanding || 0}
                </div>
              </div>
              {s >= 60 && (
                <span
                  className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-500/10 shrink-0"
                  title="High match confidence — probably the OCR variant"
                >
                  best match
                </span>
              )}
              {busy ? <Loader2 className="w-3 h-3 animate-spin text-[#1FB6C1]" /> : <Check className="w-3 h-3 text-[#1FB6C1]" />}
            </button>
          ))
        )}
      </div>
    </div>
  );
}


// 2026-02-18 Round C — PhotoStrip: per-row photo gallery.
// File input is hidden; the camera button triggers it. On select, we
// upload via /api/uploads (background-friendly: timeout 0), then attach
// to the picking_item via /api/picking/items/{iid}/photos. UI is purely
// inline (no modals), per MOBILE_CONTRACT.

/**
 * SplitPicker — Iter 411.
 *
 * Inline UI to split ONE picking row's qty_required across N rows.
 * Enforces sum-to-source so stock is never invented or lost — the
 * "Split" CTA is disabled and the total glows red until the drafts
 * match the source qty_required. Operator can add/remove split rows
 * on the fly, defaults to a 50/50 split.
 */
function SplitPicker({ item, splits, setSplits, onCancel, onSubmit, busy }) {
  const source = Number(item.qty_required) || 0;
  const nums = splits.map((s) => Number(String(s).replace(",", ".")) || 0);
  const total = nums.reduce((a, b) => a + b, 0);
  const diff = source - total;
  const valid = Math.abs(diff) < 0.01 && nums.every((n) => n > 0) && nums.length >= 2;

  const setSplit = (i, v) => {
    const next = [...splits];
    next[i] = v;
    setSplits(next);
  };
  const addRow = () => {
    const next = [...splits, "0"];
    setSplits(next);
  };
  const removeRow = (i) => {
    if (splits.length <= 2) return;
    const next = splits.filter((_, idx) => idx !== i);
    setSplits(next);
  };
  // Auto-balance the LAST row to whatever is needed to close the sum.
  const balance = () => {
    const next = [...splits];
    const others = nums.slice(0, -1).reduce((a, b) => a + b, 0);
    next[next.length - 1] = String(Math.max(0, source - others));
    setSplits(next);
  };

  return (
    <div
      className="mt-2 border border-[#1FB6C1]/40 rounded bg-[#1FB6C1]/[0.06] p-2 space-y-2"
      data-testid={`picking-split-picker-${item.id}`}
    >
      <div className="flex items-center gap-2">
        <GitBranch className="w-3.5 h-3.5 text-[#1FB6C1]" />
        <div className="text-[11px] flex-1">
          <div className="text-white font-medium">
            Split <span className="font-mono text-[#a0aabf]">{item.sku || "(no SKU)"}</span> · source qty {source} into…
          </div>
          <div className="text-[10px] text-[#647087]">
            Split values must sum to <span className="text-white font-mono">{source}</span>. Warehouse progress (qty picked) stays on the first row.
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="hv-btn hv-btn-ghost text-[10px]"
          data-testid={`picking-split-cancel-${item.id}`}
        >
          <X className="w-3 h-3" /> Cancel
        </button>
      </div>
      <div className="space-y-1">
        {splits.map((v, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[10px] text-[#647087] font-mono w-16">Row {i + 1}</span>
            <input
              type="number"
              inputMode="decimal"
              step="any"
              min="0"
              value={v}
              onChange={(e) => setSplit(i, e.target.value)}
              className="flex-1 bg-black/40 border border-white/10 rounded px-2 py-1 text-[11px] text-white font-mono focus:border-[#1FB6C1]/60 focus:outline-none"
              data-testid={`picking-split-row-${item.id}-${i}`}
            />
            {splits.length > 2 && (
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-[10px] text-rose-400 hover:text-rose-300 px-1"
                data-testid={`picking-split-remove-${item.id}-${i}`}
                title="Remove this split row"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-white/5 text-[10px]">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="hv-btn hv-btn-ghost text-[10px]"
            data-testid={`picking-split-add-${item.id}`}
          >
            + Add row
          </button>
          <button
            type="button"
            onClick={balance}
            className="hv-btn hv-btn-ghost text-[10px]"
            data-testid={`picking-split-balance-${item.id}`}
            title="Auto-set the last row to close the sum"
          >
            Auto-balance last
          </button>
        </div>
        <div className={`font-mono ${valid ? "text-emerald-300" : "text-amber-300"}`}>
          Sum: {total.toLocaleString("en-GB")} / {source}
          {!valid && diff !== 0 && (
            <span className="ml-1 text-[#647087]">
              (Δ {diff > 0 ? `+${diff}` : diff})
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !valid}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-[11px] bg-[#1FB6C1] hover:bg-[#1FB6C1]/80 text-white disabled:opacity-40 disabled:cursor-not-allowed transition"
          data-testid={`picking-split-confirm-${item.id}`}
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Split into {splits.length} rows
        </button>
      </div>
    </div>
  );
}


function PhotoStrip({ item, photos, onUploaded, onDeleted, onOpen }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = () => fileInputRef.current?.click();

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("source", "picking_proof");
        const up = await api.post(`/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,  // mobile uploads — no client-side timeout
        });
        const upload_id = up.data?.id;
        if (upload_id) {
          await api.post(`/picking/items/${item.id}/photos`, {
            upload_id, caption: "",
          });
        }
      }
      toast.success(`${files.length} photo${files.length === 1 ? "" : "s"} attached.`);
      onUploaded?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (pid) => {
    if (!window.confirm("Remove this photo?")) return;
    try {
      await api.delete(`/picking/items/${item.id}/photos/${pid}`);
      onDeleted?.();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`picking-photos-${item.id}`}>
      <button
        type="button"
        onClick={handlePick}
        disabled={uploading}
        className="hv-btn hv-btn-ghost text-[10px] disabled:opacity-50"
        data-testid={`picking-photo-add-${item.id}`}
        title="Attach proof photos to this item (mirrors to customer gallery)"
      >
        {uploading
          ? <Loader2 className="w-3 h-3 animate-spin" />
          : <Camera className="w-3 h-3" />}
        {photos.length > 0
          ? <>Photos <span className="font-mono opacity-70">({photos.length})</span></>
          : "Add photo"}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        onChange={handleFile}
        className="hidden"
        data-testid={`picking-photo-input-${item.id}`}
      />
      {photos.map((p) => (
        <div key={p.id} className="relative group">
          <button
            type="button"
            onClick={() => onOpen(p)}
            className="block w-10 h-10 rounded overflow-hidden border border-white/10 bg-white/[0.03] hover:border-[#1FB6C1]"
            data-testid={`picking-photo-thumb-${p.id}`}
            title={`${p.by_user_name || "user"} · ${p.at?.slice(0, 10) || ""}${p.caption ? ` — ${p.caption}` : ""}`}
          >
            <img src={uploadUrl(p.thumb_url || p.file_url) || p.thumb_url || p.file_url} alt="proof" className="w-full h-full object-cover" />
          </button>
          <button
            type="button"
            onClick={() => handleDelete(p.id)}
            className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-500 text-white text-[10px] opacity-0 group-hover:opacity-100 transition flex items-center justify-center"
            data-testid={`picking-photo-delete-${p.id}`}
            aria-label="Delete photo"
          >
            <Trash2 className="w-2.5 h-2.5" />
          </button>
        </div>
      ))}
    </div>
  );
}


// 2026-02-18 Round C — ProofDrawer: inline signature pad + ad-hoc
// proof upload that gets stamped on the *next* pick movement.
function ProofDrawer({ item, onSignatureSaved, onProofUploaded, signatureUploadId, proofUploadIds, onClear }) {
  const [busy, setBusy] = useState(false);

  const saveSignature = async (dataUrl) => {
    setBusy(true);
    try {
      // Convert dataURL → blob → upload as png.
      const blob = await (await fetch(dataUrl)).blob();
      const fd = new FormData();
      fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
      fd.append("source", "picking_signature");
      const up = await api.post(`/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      if (up.data?.id) onSignatureSaved(up.data.id);
    } catch (err) {
      toast.error("Signature upload failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleProofUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("source", "picking_proof_movement");
        const up = await api.post(`/uploads`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 0,
        });
        if (up.data?.id) onProofUploaded(up.data.id);
      }
      toast.success("Proof attached to next pick.");
    } catch (err) {
      toast.error("Proof upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div
      className="mt-2 border border-[#1FB6C1]/30 bg-[#1FB6C1]/[0.05] rounded p-3 space-y-2"
      data-testid={`picking-proof-drawer-${item.id}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold">
        Attach proof + signature (optional, stamps on next pick)
      </div>
      <div className="text-[10px] text-[#a0aabf]">
        Signed by: <span className="text-white font-mono">{signatureUploadId ? "✓ captured" : "—"}</span>
        {" · "}
        Proof photos: <span className="text-white font-mono">{proofUploadIds.length}</span>
      </div>
      <SignaturePad
        onSave={saveSignature}
        label="Signature (technician / receiver)"
        testIdPrefix={`picking-sig-${item.id}`}
      />
      <label className="block">
        <span className="block text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1">
          Movement-level proof photos
        </span>
        <input
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          onChange={handleProofUpload}
          disabled={busy}
          className="text-[10px] text-[#a0aabf]"
          data-testid={`picking-proof-input-${item.id}`}
        />
      </label>
      <button
        type="button"
        onClick={onClear}
        className="hv-btn hv-btn-ghost text-[10px]"
        data-testid={`picking-proof-clear-${item.id}`}
      >
        Clear staged proof
      </button>
    </div>
  );
}


// 2026-02-18 Round C — InlinePhotoLightbox (no-modal contract).
// Expands inline below the row. Tap outside or × to close.
function InlinePhotoLightbox({ photo, onClose }) {
  return (
    <div
      className="mt-2 border border-white/10 bg-black/40 rounded p-2 space-y-1"
      data-testid={`picking-photo-lightbox-${photo.id}`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-[#a0aabf]">
          {photo.by_user_name && <span>{photo.by_user_name}</span>}
          {photo.at && <span className="ml-1 font-mono">· {photo.at?.slice(0, 16).replace("T", " ")}</span>}
          {photo.caption && <span className="ml-1 italic">"{photo.caption}"</span>}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-[#647087] hover:text-white"
          data-testid="picking-photo-lightbox-close"
          aria-label="Close lightbox"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <img
        src={uploadUrl(photo.file_url) || photo.file_url}
        alt={photo.caption || "proof"}
        className="max-h-96 w-auto mx-auto rounded"
      />
    </div>
  );
}


function QtyCell({ label, value, unit, highlight = "#647087" }) {
  return (
    <div className="bg-white/[0.02] rounded px-1.5 py-1 border border-white/5">
      <div className="uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="font-mono text-[11px]" style={{ color: highlight }}>
        {value ?? 0} <span className="text-[9px] opacity-60">{unit || "pcs"}</span>
      </div>
    </div>
  );
}


function BreakdownInline({ data }) {
  return (
    <div className="space-y-4 text-xs border border-white/5 rounded p-3 bg-white/[0.02]"
         data-testid="picking-breakdown-inline">
      <div className="text-[10px] uppercase tracking-wider text-[#647087]">
        Generated {data.generated_at} · Grand total used <span className="text-[#10B981] font-mono">{data.grand_total_used}</span>
      </div>
      {(data.sections || []).map((sec) => (
        <div key={sec.section}>
          <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-1">{sec.section}</div>
          <div className="border border-white/5 rounded overflow-x-auto">
            <table className="w-full text-[11px] min-w-[480px]">
              <thead className="bg-white/[0.04] text-[9px] uppercase tracking-wider text-[#647087]">
                <tr>
                  <th className="text-left p-1.5">Item</th>
                  <th className="text-right p-1.5">Req</th>
                  <th className="text-right p-1.5">Pick</th>
                  <th className="text-right p-1.5">Ret</th>
                  <th className="text-right p-1.5">Used</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {sec.rows.map((r) => (
                  <tr key={r.item_id}>
                    <td className="p-1.5">
                      <div className="text-white truncate max-w-[260px]">{r.name}</div>
                      <div className="text-[9px] text-[#647087] font-mono">
                        {r.sku || ""}{r.is_non_stock ? ` · ${r.non_stock_kind}` : ""}
                      </div>
                    </td>
                    <td className="p-1.5 text-right font-mono">{r.qty_required}</td>
                    <td className="p-1.5 text-right font-mono text-emerald-300">{r.qty_picked}</td>
                    <td className="p-1.5 text-right font-mono text-purple-300">{r.qty_returned}</td>
                    <td className="p-1.5 text-right font-mono text-white font-bold">{r.qty_used}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}


function InlineAddRow({ listId, onCancel, onAdded }) {
  // Iter 290zc — full rewrite with:
  //   • inventory type-ahead (re-uses /api/inventory/resolve same as the
  //     JobCard add-material flow), so SKU + name + unit auto-fill on pick
  //   • multi-add — after a successful save the picked item resets but
  //     the panel stays open so you can keep adding rows without
  //     re-clicking the Add button
  //   • last-added micro-confirmation strip so the user sees what just
  //     landed and can undo a misclick fast
  const [text, setText] = useState("");
  const [picked, setPicked] = useState(null);   // {inventory_id, name, sku, unit, on_hand}
  const [candidates, setCandidates] = useState([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [searching, setSearching] = useState(false);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState("");
  const [isNonStock, setIsNonStock] = useState(false);
  const [nonStockKind, setNonStockKind] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);   // {id, label}
  const debounce = useRef(null);

  useEffect(() => {
    if (!text.trim() || picked) {
      setCandidates([]);
      return;
    }
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.post("/inventory/resolve", { text: text.trim(), limit: 50 });
        setCandidates(data.candidates || []);
        setTotalMatches(data.total_matches ?? (data.candidates || []).length);
      } catch (_e) {
        setCandidates([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [text, picked]);

  const pick = (c) => {
    setPicked(c);
    setText(c.name);
    setIsNonStock(false);
    setCandidates([]);
  };

  const resetForNext = () => {
    setText(""); setPicked(null); setQty(1); setNotes("");
    setIsNonStock(false); setNonStockKind("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const nameTrimmed = (picked?.name || text || "").trim();
    if (!nameTrimmed) {
      toast.error("Type or pick an item first.");
      return;
    }
    if (Number(qty) <= 0) {
      toast.error("Qty must be > 0.");
      return;
    }
    setBusy(true);
    try {
      const payload = picked
        ? {
            inventory_id: picked.inventory_id,
            sku: picked.sku || "",
            name: picked.name,
            qty_required: parseFloat(qty) || 0,
            unit: picked.unit || "pcs",
            notes,
            source: "manual",
          }
        : {
            name: nameTrimmed,
            qty_required: parseFloat(qty) || 0,
            unit: "pcs",
            notes: notes || "non-stock — no inventory match",
            is_non_stock: !!isNonStock,
            non_stock_kind: nonStockKind || (isNonStock ? "non-stock" : ""),
            source: "manual",
          };
      const r = await api.post(`/picking/lists/${listId}/items`, payload);
      const created = r.data?.id ? r.data : r.data?.items?.[0];
      setLastAdded({ id: created?.id, label: payload.name });
      toast.success("Row added.");
      resetForNext();
      onAdded?.({ keepOpen: true });
    } catch (err) {
      // Iter 398g — Dirk's ask: warn on duplicate, offer to edit
      // existing instead of creating a phantom second row. Backend
      // returns 409 with { code: "duplicate_row", existing: {...} }.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail && detail.code === "duplicate_row") {
        const existing = detail.existing;
        const desiredQty = parseFloat(qty) || 0;
        const scrollAndFlash = () => {
          setTimeout(() => {
            const el = document.querySelector(`[data-testid^="picking-row-${existing.id}"]`);
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
            el?.classList?.add("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-black");
            setTimeout(() => el?.classList?.remove("ring-2", "ring-amber-400", "ring-offset-2", "ring-offset-black"), 2600);
          }, 100);
        };
        const choice = window.confirm(
          `"${existing.name}" is already on this picking list (qty ${existing.qty_required}).\n\n`
          + `OK  → Add +${desiredQty} to the existing row (recommended)\n`
          + `Cancel → I'll decide (you can force-create a duplicate from the row menu)`
        );
        if (choice) {
          try {
            await api.post(`/picking/items/${existing.id}/increment-qty`, {
              delta: desiredQty,
              note: `merged duplicate add via inline row`,
            });
            toast.success(`Added +${desiredQty} to existing row (${existing.qty_required + desiredQty} total).`);
            resetForNext();
            scrollAndFlash();
            onAdded?.({ keepOpen: true });
          } catch (err2) {
            toast.error(err2?.response?.data?.detail || "Merge failed.");
          }
        } else {
          scrollAndFlash();
          toast.info("Left it as-is. Row highlighted for review.");
        }
      } else {
        toast.error(
          (typeof detail === "string" ? detail : detail?.message)
          || "Add failed.",
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="space-y-2 text-xs border border-[#1FB6C1]/30 bg-[#1FB6C1]/5 rounded-md p-3"
      data-testid="picking-add-row-inline"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider text-[#1FB6C1] font-bold">
          Add picking row · keeps panel open for multi-add
        </div>
        <button type="button" onClick={onCancel} className="text-[#647087] hover:text-white">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {lastAdded && (
        <div className="text-[10px] text-emerald-300 italic" data-testid="picking-add-last">
          ✓ Added: {lastAdded.label}
        </div>
      )}

      {/* Inventory type-ahead — pulls SKU + unit + on_hand from the
          resolved inventory item. Type-then-pick or type-then-submit
          for free-text non-stock entries. */}
      <Field label="Search inventory or type a description">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#647087]" />
          <input
            className="hv-input w-full text-sm pl-7"
            value={text}
            onChange={(e) => { setText(e.target.value); if (picked) setPicked(null); }}
            autoFocus
            placeholder="e.g. 'Grundfos JP4' or 'P*UV55W'"
            data-testid="picking-add-search"
          />
          {searching && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-[#647087]" />
          )}
        </div>
      </Field>

      {candidates.length > 0 && (
        <>
          <div className="text-[10px] text-[#647087] flex items-center gap-1.5">
            {totalMatches > candidates.length
              ? <>Showing top {candidates.length} of <span className="text-cyan-300 font-mono">{totalMatches}</span> matches · refine search to narrow down</>
              : <>{candidates.length} {candidates.length === 1 ? "match" : "matches"}</>}
          </div>
          <div
            className="border border-white/10 rounded bg-[#0a0b0e] divide-y divide-white/5 max-h-72 overflow-auto"
            data-testid="picking-add-candidates"
          >
          {candidates.map((c) => (
            <button
              key={c.inventory_id}
              type="button"
              onClick={() => pick(c)}
              className="w-full text-left px-2.5 py-1.5 hover:bg-white/[0.04] text-xs flex items-center gap-2"
              data-testid={`picking-add-candidate-${c.inventory_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-white truncate">{c.name}</div>
                <div className="text-[9px] text-[#647087] font-mono mt-0.5">
                  {c.sku || "—"}
                  {typeof c.on_hand !== "undefined" && <span className="ml-1.5 text-cyan-300">on hand · {c.on_hand}</span>}
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-wider opacity-70">{c.score_tier || "match"}</span>
            </button>
          ))}
          </div>
        </>
      )}

      {picked && (
        <div className="rounded border border-emerald-400/30 bg-emerald-500/[0.06] p-2 flex items-center gap-2 text-[11px]" data-testid="picking-add-picked">
          <Check className="w-3 h-3 text-emerald-300" />
          <div className="flex-1 min-w-0">
            <div className="text-white truncate">{picked.name}</div>
            <div className="text-[9px] text-[#647087] font-mono">{picked.sku || "no SKU"} · {picked.unit || "pcs"}{typeof picked.on_hand !== "undefined" && ` · on hand ${picked.on_hand}`}</div>
          </div>
          <button type="button" onClick={() => { setPicked(null); setText(""); }} className="text-[#647087] hover:text-white" title="Clear">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Field label="Qty required">
          <input
            type="number" min="0" step="0.01"
            className="hv-input w-full text-sm"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            data-testid="picking-add-qty"
          />
        </Field>
        <Field label="Unit">
          <input
            className="hv-input w-full text-sm"
            value={picked?.unit || "pcs"}
            disabled={!!picked}
            onChange={(e) => { if (!picked) setQty(qty); /* unit lives in payload only */ }}
            data-testid="picking-add-unit"
          />
        </Field>
      </div>

      {!picked && (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={isNonStock}
            onChange={(e) => setIsNonStock(e.target.checked)}
            data-testid="picking-add-non-stock"
          />
          Non-stock item (gasket, sikaflex, bolts, provision…)
        </label>
      )}
      {!picked && isNonStock && (
        <Field label="Non-stock type (free text — e.g. 'gasket', 'thread tape', 'custom bracket')">
          <input
            type="text"
            className="hv-input w-full text-sm"
            value={nonStockKind}
            onChange={(e) => setNonStockKind(e.target.value)}
            placeholder="Describe what kind of item this is — anything goes"
            data-testid="picking-add-non-stock-kind"
          />
        </Field>
      )}

      <Field label="Notes (optional)">
        <input
          className="hv-input w-full text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid="picking-add-notes"
        />
      </Field>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="hv-btn hv-btn-ghost text-xs">
          Done
        </button>
        <button
          type="submit"
          disabled={busy || !(picked?.name || text.trim())}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="picking-add-submit"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          Add another
        </button>
      </div>
    </form>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#a0aabf] mb-1">{label}</span>
      {children}
    </label>
  );
}


function InlineMovementHistory({ itemId }) {
  const [movements, setMovements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/picking/items/${itemId}/movements`);
        if (alive) setMovements(r.data?.items || []);
      } catch (err) {
        if (alive) setMovements([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [itemId]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  return (
    <div className="mt-2 pt-2 border-t border-white/5"
         data-testid={`picking-history-inline-${itemId}`}>
      <div className="text-[9px] uppercase tracking-wider text-[#1FB6C1] font-bold mb-1.5 flex items-center gap-1">
        <History className="w-3 h-3" /> Movement history
      </div>
      {loading && (
        <div className="text-[10px] text-[#647087]">
          <Loader2 className="w-3 h-3 animate-spin inline" /> Loading…
        </div>
      )}
      {!loading && movements && movements.length === 0 && (
        <div className="text-[10px] text-[#647087] italic">
          No movements yet. Pick or return to populate.
        </div>
      )}
      {!loading && movements && movements.length > 0 && (
        <div className="relative pl-4">
          <div className="absolute left-1.5 top-0.5 bottom-0.5 w-px bg-white/10" aria-hidden="true" />
          {movements.map((mv) => {
            // Iter 398e — history now surfaces add / remove / unassign /
            // dispatch / dispatch_reverse in addition to pick / return.
            // Colours + labels sit alongside so Dirk's team can scan
            // "who did what, when" at a glance.
            const styleMap = {
              pick:              { c: "#10B981", l: "Picked",     Icon: ArrowDownToLine },
              pick_reverse:      { c: "#10B981", l: "Picked −",   Icon: ArrowUpFromLine },
              return:            { c: "#A78BFA", l: "Returned",   Icon: ArrowUpFromLine },
              return_reverse:    { c: "#A78BFA", l: "Returned −", Icon: ArrowDownToLine },
              dispatch:          { c: "#38BDF8", l: "Dispatched", Icon: ArrowUpFromLine },
              dispatch_reverse:  { c: "#38BDF8", l: "Dispatch −", Icon: ArrowDownToLine },
              add:               { c: "#1FB6C1", l: "Added",      Icon: ArrowDownToLine },
              remove:            { c: "#F43F5E", l: "Removed",    Icon: ArrowUpFromLine },
              unassign:          { c: "#F59E0B", l: "Unassigned", Icon: ArrowUpFromLine },
            };
            const s = styleMap[mv.kind] || { c: "#647087", l: mv.kind || "Event", Icon: ArrowDownToLine };
            const showQty = mv.qty != null && mv.kind !== "unassign";
            return (
              <div key={mv.id} className="relative pb-1.5"
                   data-testid={`picking-movement-${mv.id}`}>
                <div className="absolute -left-[10px] top-1.5 w-2 h-2 rounded-full"
                     style={{ background: s.c, boxShadow: `0 0 0 2px ${s.c}22` }} />
                <div className="text-[10px] flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                  <s.Icon className="w-2.5 h-2.5" style={{ color: s.c }} />
                  <span className="uppercase tracking-wider font-bold" style={{ color: s.c }}>
                    {s.l}
                  </span>
                  {showQty && <span className="font-mono text-white">{mv.qty}</span>}
                  <span className="text-[#647087]">by {mv.by_user_name || (mv.by_user_id || "").slice(0, 8)}</span>
                  {mv.note && <span className="text-[#a0aabf] italic">&ldquo;{mv.note}&rdquo;</span>}
                  <span className="text-[#647087] ml-auto">{formatDate(mv.at)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


/**
 * BulkActionBar — Iter 290zh.
 * Three primary actions visible at the top of every picking list:
 *   • Pick all outstanding (N)
 *   • Dispatch all picked (M)
 *   • Sign off & complete (opens inline signature pad)
 *
 * Mirrors how Cin7 / ShipHero / Odoo expose batch operations: one tap
 * per stage, optimistic toast on success, no modals (matches the
 * project's no-modal contract).
 */
function BulkActionBar({ listId, totals, status, onChanged }) {
  const [busy, setBusy] = useState(null);  // 'pick' | 'dispatch' | 'signoff' | 'photo' | null
  const [signOffOpen, setSignOffOpen] = useState(false);
  const [signOffName, setSignOffName] = useState("");
  const [signOffNotes, setSignOffNotes] = useState("");
  const [alsoDispatch, setAlsoDispatch] = useState(true);
  // Iter 290zk — bulk sign-off proof photo. ONE image for the whole list
  // (vs. per-row PhotoStrip). Used when the picker stages a pallet/pile
  // and snaps a single overview shot at hand-off, instead of taking 300
  // photos for 300 tiny fittings.
  const [bulkUpload, setBulkUpload] = useState(null);   // {id, file_url, thumb_url, filename}
  const bulkFileRef = useRef(null);

  const outstandingRows = totals.rows_outstanding || 0;
  const outstandingQty = totals.qty_outstanding || 0;
  const pendingDispatchQty = totals.qty_pending_dispatch || 0;
  const isClosed = status === "closed";

  const pickAll = async () => {
    if (!outstandingRows || isClosed) return;
    if (!window.confirm(`Pick all ${outstandingQty} outstanding across ${outstandingRows} row${outstandingRows === 1 ? "" : "s"}?`)) return;
    setBusy("pick");
    try {
      const { data } = await api.post(`/picking/lists/${listId}/pick-all`);
      toast.success(`Picked ${data.qty_picked} across ${data.items_picked} rows.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pick-all failed.");
    } finally {
      setBusy(null);
    }
  };

  const dispatchAll = async () => {
    if (!pendingDispatchQty || isClosed) return;
    if (!window.confirm(`Dispatch ${pendingDispatchQty} (everything picked, not yet dispatched)?`)) return;
    setBusy("dispatch");
    try {
      const { data } = await api.post(`/picking/lists/${listId}/dispatch-all`);
      toast.success(`Dispatched ${data.qty_dispatched} across ${data.items_dispatched} rows.`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dispatch-all failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleBulkPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy("photo");
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("source", "picking_signoff");
      const up = await api.post(`/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      setBulkUpload(up.data);
      toast.success("Sign-off photo attached.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setBusy(null);
      if (bulkFileRef.current) bulkFileRef.current.value = "";
    }
  };

  const signOff = async () => {
    setBusy("signoff");
    try {
      await api.post(`/picking/lists/${listId}/sign-off`, {
        sign_off_name: signOffName || undefined,
        notes: signOffNotes || undefined,
        also_dispatch: alsoDispatch,
        mark_complete: true,
        signature_upload_id: bulkUpload?.id || undefined,
      });
      toast.success("Picking signed off.");
      setSignOffOpen(false);
      setBulkUpload(null);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="hv-card p-2.5 space-y-2" data-testid="picking-bulk-action-bar">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <button
          type="button"
          onClick={pickAll}
          disabled={!outstandingRows || isClosed || busy !== null}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed py-2"
          data-testid="picking-bulk-pick-all"
          title={outstandingRows ? `${outstandingQty} units across ${outstandingRows} rows` : "Nothing outstanding"}
        >
          {busy === "pick" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
          Pick all <span className="font-mono">({outstandingRows})</span>
        </button>
        <button
          type="button"
          onClick={dispatchAll}
          disabled={!pendingDispatchQty || isClosed || busy !== null}
          className="hv-btn text-xs disabled:opacity-40 disabled:cursor-not-allowed py-2"
          style={{ background: pendingDispatchQty && !isClosed ? "#0284c7" : undefined, color: "white" }}
          data-testid="picking-bulk-dispatch-all"
          title={pendingDispatchQty ? `${pendingDispatchQty} pending dispatch` : "Nothing pending dispatch"}
        >
          {busy === "dispatch" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpFromLine className="w-3.5 h-3.5" />}
          Dispatch all <span className="font-mono">({fmtN(pendingDispatchQty)})</span>
        </button>
        <button
          type="button"
          onClick={() => setSignOffOpen((v) => !v)}
          disabled={isClosed || busy !== null}
          className="hv-btn hv-btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed py-2"
          data-testid="picking-bulk-signoff-toggle"
        >
          <PenLine className="w-3.5 h-3.5" />
          {isClosed ? "Closed ✓" : (signOffOpen ? "Close panel" : "Sign off & complete")}
        </button>
      </div>

      {/* Inline sign-off panel (no modal — Trust Contract) */}
      {signOffOpen && !isClosed && (
        <div className="border border-cyan-400/30 bg-cyan-500/[0.04] rounded p-3 space-y-2" data-testid="picking-signoff-panel">
          <div className="text-[10px] uppercase tracking-wider text-cyan-300 font-bold">Sign off picking list</div>
          <input
            value={signOffName}
            onChange={(e) => setSignOffName(e.target.value)}
            placeholder="Signed by (full name) — optional"
            className="hv-input w-full text-xs"
            data-testid="picking-signoff-name"
          />
          <DictTextarea
            value={signOffNotes}
            onChange={(e) => setSignOffNotes(e.target.value)}
            placeholder="Notes / short-pick reasons — optional"
            className="hv-input w-full text-xs"
            rows={2}
            data-testid="picking-signoff-notes"
          />
          <label className="flex items-center gap-1.5 text-[11px] text-[#a0aabf]">
            <input
              type="checkbox"
              checked={alsoDispatch}
              onChange={(e) => setAlsoDispatch(e.target.checked)}
              data-testid="picking-signoff-also-dispatch"
            />
            Also dispatch everything picked (recommended when goods leave with the tech)
          </label>

          {/* Iter 290zk — bulk sign-off proof photo (ONE image for the whole list).
              Sits next to the signature field because picking 300 tiny fittings
              doesn't warrant a photo per row. One overview shot of the staged
              goods (or signed delivery note) is enough proof. */}
          <div className="border border-white/10 rounded p-2 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-wider text-[#647087] font-bold mb-1.5">
              Sign-off photo · one image for the whole list (optional)
            </div>
            {bulkUpload ? (
              <div className="flex items-center gap-2">
                <img
                  src={uploadUrl(bulkUpload.thumb_url || bulkUpload.file_url) || bulkUpload.thumb_url || bulkUpload.file_url}
                  alt="sign-off proof"
                  className="w-14 h-14 rounded object-cover border border-emerald-400/30"
                  data-testid="picking-signoff-photo-preview"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] text-emerald-300 truncate">✓ {bulkUpload.filename || "Photo attached"}</div>
                  <div className="text-[10px] text-[#647087]">Will be stamped on the picking list when you confirm.</div>
                </div>
                <button
                  type="button"
                  onClick={() => setBulkUpload(null)}
                  className="text-rose-400 hover:text-rose-300 text-[10px] shrink-0"
                  data-testid="picking-signoff-photo-clear"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => bulkFileRef.current?.click()}
                disabled={busy === "photo"}
                className="hv-btn hv-btn-ghost text-xs w-full disabled:opacity-50"
                data-testid="picking-signoff-photo-add"
              >
                {busy === "photo"
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Camera className="w-3.5 h-3.5" />}
                Upload sign-off photo
              </button>
            )}
            <input
              ref={bulkFileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleBulkPhoto}
              className="hidden"
              data-testid="picking-signoff-photo-input"
            />
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setSignOffOpen(false)} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
            <button
              type="button"
              onClick={signOff}
              disabled={busy === "signoff"}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="picking-signoff-confirm"
            >
              {busy === "signoff" ? <Loader2 className="w-3 h-3 animate-spin" /> : <PenLine className="w-3 h-3" />}
              Confirm sign-off
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
